import { fetchAuthSession } from 'aws-amplify/auth';

import { WHISPERBI_SECURITY_API } from '../env';

// API Base URL for source connections (security service)
const API_BASE_URL = WHISPERBI_SECURITY_API;
const SOURCE_CONNECTIONS_BASE_URL = `${API_BASE_URL}/sourceconnections`;

// Connection types supported
const CONNECTION_TYPES = {
    ORACLE: 'oracle',
    SQL_SERVER: 'sqlserver',
    MYSQL: 'mysql',
    POSTGRESQL: 'postgresql',
    AURORA: 'aurora',
    SNOWFLAKE: 'snowflake',
    REDSHIFT: 'redshift',
    BIGQUERY: 'bigquery',
    SYNAPSE: 'synapse'
};

// Get current user from auth session
const getCurrentUser = async () => {
    try {
        const session = await fetchAuthSession();
        const idTokenPayload = session.tokens?.idToken?.payload;
        const accessTokenPayload = session.tokens?.accessToken?.payload;
        const cognitoId = idTokenPayload?.sub || accessTokenPayload?.sub;
        if (cognitoId) {
            return cognitoId;
        }
        return idTokenPayload?.['cognito:username'] || 'unknown_user';
    } catch (error) {
        console.error('Error getting current user:', error);
        return 'unknown_user';
    }
};

// Get authorization headers for API calls
const getAuthHeaders = async () => {
    try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
        };
    } catch (error) {
        console.error('Error getting auth headers:', error);
        throw new Error('Authentication required');
    }
};

// Make authenticated API request
const makeApiRequest = async (url, options = {}) => {
    try {
        console.log('makeApiRequest - URL:', url);
        console.log('makeApiRequest - Method:', options.method || 'GET');
        
        const headers = await getAuthHeaders();
        
        const response = await fetch(url, {
            ...options,
            headers: {
                ...headers,
                ...options.headers
            }
        });

        console.log('makeApiRequest - Response status:', response.status);
        console.log('makeApiRequest - Response URL:', response.url);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('API request failed:', error);
        throw error;
    }
};

// Snowflake: par de claves (generado por WhisperBI -> generated_key_token,
// o subido -> private_key_pem), sin contraseña, con warehouse/rol/schema.
// Ver whisperbi-back-security/functions/source-connections/snowflake_connect.py.
const SNOWFLAKE_FIELDS = ['auth_method', 'warehouse', 'role', 'schema', 'generated_key_token', 'private_key_pem', 'private_key_passphrase'];
const withSnowflakeFields = (requestBody, source) => {
    if (source.connection_type !== 'snowflake') return requestBody;
    const body = { ...requestBody, auth_method: 'key_pair' };
    delete body.password;
    SNOWFLAKE_FIELDS.forEach((field) => {
        if (source[field] !== undefined && source[field] !== '') body[field] = source[field];
    });
    return body;
};
const redact = (body) => ({ ...body, password: '***', private_key_pem: body.private_key_pem ? '***' : undefined,
    private_key_passphrase: body.private_key_passphrase ? '***' : undefined,
    generated_key_token: body.generated_key_token ? '***' : undefined });

// Create a new database connection record using the new API
export const createDatabaseConnection = async (connectionData) => {
    try {
        // SECURITY (fix HIGH-05, auditoría 2026-09-07): no loguear el
        // password real de la conexión -- antes se logueaba tal cual,
        // sin redactar (a diferencia de testDatabaseConnectionParameters,
        // más abajo en este mismo archivo, que ya lo hacía bien).
        console.log('#### createDatabaseConnection:', { ...connectionData, password: '***' });

        // Validate required fields for the new API (Snowflake: sin contraseña,
        // con par de claves -- lo valida el backend).
        const requiredFields = connectionData.connection_type === 'snowflake'
            ? ['enterprise_application_name', 'connection_type', 'database_name', 'host', 'username', 'warehouse', 'role']
            : ['enterprise_application_name', 'connection_type', 'database_name', 'host', 'username', 'password'];
        const missingFields = requiredFields.filter(field => !connectionData[field]);

        if (missingFields.length > 0) {
            throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
        }

        // Validate connection type
        if (!Object.values(CONNECTION_TYPES).includes(connectionData.connection_type)) {
            throw new Error(`Invalid connection type: ${connectionData.connection_type}`);
        }

        // Prepare request body for the new API (no more secret_manager_name)
        const requestBody = withSnowflakeFields({
            enterprise_application_name: connectionData.enterprise_application_name,
            connection_type: connectionData.connection_type,
            database_name: connectionData.database_name,
            database_version: connectionData.database_version || '',
            description: connectionData.description || '',
            host: connectionData.host,
            username: connectionData.username,
            password: connectionData.password,
            is_public: connectionData.is_public === true,
            use_ssl: connectionData.use_ssl !== false
        }, connectionData);

        console.log('##### Request body for new API:', JSON.stringify(redact(requestBody), null, 2));

        // Make API call to create connection
        const response = await makeApiRequest(SOURCE_CONNECTIONS_BASE_URL, {
            method: 'POST',
            body: JSON.stringify(requestBody)
        });

        return {
            success: true,
            data: response
        };

    } catch (error) {
        console.error('Error creating database connection:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

// Update an existing database connection using the new API
export const updateDatabaseConnection = async (connectionId, updateData) => {
    try {
        console.log('** updateDatabaseConnection.connectionId:', connectionId);
        // SECURITY (fix HIGH-05): ver mismo fix en createDatabaseConnection.
        console.log('** updateDatabaseConnection.updateData:', { ...updateData, password: '***' });

        if (!connectionId) {
            throw new Error('Connection ID is required');
        }

        // Prepare request body for the new API
        const requestBody = {};
        
        // Add fields that can be updated
        const allowedFields = [
            'enterprise_application_name',
            'connection_type', 
            'database_name',
            'database_version',
            'description',
            'host',
            'username',
            'password',
            'is_public',
            'use_ssl',
            // Conexiones de archivo: modo de carga ('replace' | 'append')
            'load_mode',
            // Snowflake (ver withSnowflakeFields)
            ...SNOWFLAKE_FIELDS
        ];

        allowedFields.forEach(field => {
            if (updateData[field] !== undefined) {
                requestBody[field] = updateData[field];
            }
        });

        if (requestBody.connection_type === 'snowflake') {
            delete requestBody.password;
            requestBody.auth_method = 'key_pair';
        }

        console.log('Request body for update API:', JSON.stringify(redact(requestBody), null, 2));

        // Make API call to update connection
        const response = await makeApiRequest(`${SOURCE_CONNECTIONS_BASE_URL}/${connectionId}`, {
            method: 'PUT',
            body: JSON.stringify(requestBody)
        });

        return {
            success: true,
            data: response
        };

    } catch (error) {
        console.error('Error updating database connection:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

// Get a specific database connection by ID using the new API
export const getDatabaseConnection = async (connectionId) => {
    console.log('** getDatabaseConnection.connectionId:', connectionId);

    if (!connectionId) {
        return {
            success: false,
            error: 'Connection ID is required'
        };
    }

    // BUGFIX (2026-09-15): antes esta función pedía el LISTADO completo
    // (/by-user/{id}/connection) y hacía response.find(...) para ubicar la
    // conexión -- desde la unificación de forma de respuesta (2026-09-08,
    // ver template.yaml junto a GetSourceConnections) ese endpoint siempre
    // devuelve un objeto {connections, count, ...}, nunca un array pelado,
    // así que .find ya no existe ahí y esto tiraba
    // "response.find is not a function" cada vez que se abría "Modificar".
    // El endpoint dedicado por ID (GET /sourceconnections/{id}) ya existe
    // y es el mismo que usan getRawSourceConnection/pollConnectionStatus
    // más abajo en este archivo -- se reusa en vez de traer todo el listado.
    return getSourceConnectionById(connectionId);
};

// Get all database connections for a user using the new API
export const getDatabaseConnectionsByUser = async (createdBy = null) => {
    try {
        const currentUser = createdBy || await getCurrentUser();
        console.log('** getDatabaseConnectionsByUser.createdBy:', currentUser);

        // Make API call to get connections for the user
        // Updated path to match backend: /sourceconnections/by-user/{cognito_user_id}/connection
        const url = `${SOURCE_CONNECTIONS_BASE_URL}/by-user/${currentUser}/connection`;
        console.log('** getDatabaseConnectionsByUser - Full URL:', url);
        const response = await makeApiRequest(url);

        // El backend devuelve `{ connections: [...], next_cursor }`. Antes
        // devolvía un array pelado cuando no había página siguiente y un
        // objeto cuando sí, y acá se asumía siempre array -- el caller hace
        // `result.data.map(...)`, así que reventaba en cuanto un tenant
        // pasaba de una página. Se aceptan las dos formas: la nueva y la
        // vieja, por si queda un backend sin desplegar.
        const data = Array.isArray(response)
            ? response
            : (response?.connections || response?.data || []);

        return {
            success: true,
            data,
            nextCursor: response?.next_cursor || response?.next_token || null
        };

    } catch (error) {
        console.error('Error getting database connections by user:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

// Soft delete a database connection using the new API
export const softDeleteDatabaseConnection = async (connectionId) => {
    try {
        console.log('** softDeleteDatabaseConnection.connectionId:', connectionId);

        if (!connectionId) {
     throw new Error('Connection ID is required');
        }

        // Make API call to delete connection
        const response = await makeApiRequest(`${SOURCE_CONNECTIONS_BASE_URL}/${connectionId}`, {
            method: 'DELETE'
        });

        return {
            success: true,
            data: response
        };

    } catch (error) {
        console.error('Error soft deleting database connection:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

// Snowflake: genera un par de claves RSA en el backend. Devuelve la clave
// pública, su huella, el SQL para el usuario de servicio y la privada SOLO
// como token cifrado (KMS, atado al tenant). key_slot 2 = rotación.
export const generateSnowflakeKeyPair = async ({ username, role, warehouse, databaseName, keySlot = 1 } = {}) => {
    return makeApiRequest(`${SOURCE_CONNECTIONS_BASE_URL}/snowflake/keypair`, {
        method: 'POST',
        body: JSON.stringify({ username, role, warehouse, database_name: databaseName, key_slot: keySlot })
    });
};

// Snowflake: prueba una conexión guardada con una clave CANDIDATA (rotación),
// sin guardarla todavía.
export const testSnowflakeCandidateKey = async (connectionId, generatedKeyToken) => {
    return makeApiRequest(`${SOURCE_CONNECTIONS_BASE_URL}/${connectionId}/test`, {
        method: 'POST',
        body: JSON.stringify({ generated_key_token: generatedKeyToken })
    });
};

// Test a database connection using the new test endpoint
export const testDatabaseConnection = async (connectionId) => {
    try {
        console.log('** testDatabaseConnection.connectionId:', connectionId);

        if (!connectionId) {
            throw new Error('Connection ID is required');
        }

        // Make API call to test the connection using the new dedicated test endpoint
        const response = await makeApiRequest(`${SOURCE_CONNECTIONS_BASE_URL}/${connectionId}/test`, {
            method: 'POST',
            body: JSON.stringify({}) // Empty body for testing existing connection
        });

        console.log('** testDatabaseConnection - Backend response:', response);
        console.log('** testDatabaseConnection - Test result:', response.test_result);
        console.log('** testDatabaseConnection - Test success:', response.test_result?.success);
        console.log('** testDatabaseConnection - Test message:', response.test_result?.message);

        return {
            success: true,
            data: response
        };

    } catch (error) {
        console.error('Error testing database connection:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

// Test database connection parameters without saving (for form validation)
export const testDatabaseConnectionParameters = async (connectionParams) => {
    try {
        console.log('** testDatabaseConnectionParameters:', { ...connectionParams, password: '***' });

        // Validate required fields
        const requiredFields = connectionParams.connection_type === 'snowflake'
            ? ['connection_type', 'host', 'username', 'database_name', 'warehouse', 'role']
            : ['connection_type', 'host', 'username', 'password', 'database_name'];
        const missingFields = requiredFields.filter(field => !connectionParams[field]);

        if (missingFields.length > 0) {
            throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
        }

        // Prepare request body for testing
        const requestBody = withSnowflakeFields({
            connection_type: connectionParams.connection_type,
            host: connectionParams.host,
            port: connectionParams.port,
            database_name: connectionParams.database_name,
            username: connectionParams.username,
            password: connectionParams.password,
            use_ssl: connectionParams.use_ssl !== false
        }, connectionParams);

        console.log('Testing connection with params:', redact(requestBody));

        // Make API call to test endpoint without connection ID
        const response = await makeApiRequest(`${SOURCE_CONNECTIONS_BASE_URL}/test`, {
            method: 'POST',
            body: JSON.stringify(requestBody)
        });

        return {
            success: true,
            data: response
        };

    } catch (error) {
        console.error('Error testing database connection parameters:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

// Create a new file-based source connection record (bypasses DB-specific field validation)
export const createFileConnectionRecord = async (connectionData) => {
    try {
        console.log('#### createFileConnectionRecord:', connectionData);

        // Validate only the fields required for file connections
        if (!connectionData.enterprise_application_name) {
            throw new Error('Missing required field: enterprise_application_name');
        }
        if (!connectionData.connection_type) {
            throw new Error('Missing required field: connection_type');
        }

        const requestBody = {
            enterprise_application_name: connectionData.enterprise_application_name,
            connection_type: connectionData.connection_type,
            description: connectionData.description || '',
            is_public: connectionData.is_public === true,
        };

        // Include csv_delimiter only for csv connections
        if (connectionData.connection_type === 'csv' && connectionData.csv_delimiter !== undefined) {
            requestBody.csv_delimiter = connectionData.csv_delimiter;
        }

        // Wizard de carga con schema/nombre de tabla explícitos (ver
        // FileUploadWizard) -- opcionales.
        if (connectionData.iceberg_schema_id) {
            requestBody.iceberg_schema_id = connectionData.iceberg_schema_id;
        }
        if (connectionData.table_name_override) {
            requestBody.table_name_override = connectionData.table_name_override;
        }
        if (connectionData.sheet_table_name_overrides) {
            requestBody.sheet_table_name_overrides = connectionData.sheet_table_name_overrides;
        }

        console.log('##### File connection request body:', JSON.stringify(requestBody, null, 2));

        const response = await makeApiRequest(SOURCE_CONNECTIONS_BASE_URL, {
            method: 'POST',
            body: JSON.stringify(requestBody)
        });

        return {
            success: true,
            data: response
        };
    } catch (error) {
        console.error('Error creating file connection record:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

// Get a presigned URL for uploading a file to S3
export const getFileUploadUrl = async (sourceConnectionId, fileName, contentType) => {
    try {
        console.log('#### getFileUploadUrl:', { sourceConnectionId, fileName, contentType });

        const requestBody = {
            source_connection_id: sourceConnectionId,
            file_name: fileName,
            content_type: contentType || 'application/octet-stream'
        };

        const response = await makeApiRequest(`${SOURCE_CONNECTIONS_BASE_URL}/file-upload-url`, {
            method: 'POST',
            body: JSON.stringify(requestBody)
        });

        return {
            success: true,
            data: response
        };
    } catch (error) {
        console.error('Error getting file upload URL:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

// Confirm that a file has been uploaded to S3
export const confirmFileUpload = async (sourceConnectionId, s3Key) => {
    try {
        console.log('#### confirmFileUpload:', { sourceConnectionId, s3Key });

        const requestBody = { s3_key: s3Key };

        const response = await makeApiRequest(
            `${SOURCE_CONNECTIONS_BASE_URL}/${sourceConnectionId}/confirm-upload`,
            {
                method: 'PUT',
                body: JSON.stringify(requestBody)
            }
        );

        return {
            success: true,
            data: response
        };
    } catch (error) {
        console.error('Error confirming file upload:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

// Get a specific source connection by its ID directly
export const getSourceConnectionById = async (connectionId) => {
    try {
        console.log('#### getSourceConnectionById:', connectionId);

        if (!connectionId) {
            throw new Error('Connection ID is required');
        }

        const response = await makeApiRequest(
            `${SOURCE_CONNECTIONS_BASE_URL}/${connectionId}`
        );

        return {
            success: true,
            data: response
        };
    } catch (error) {
        console.error('Error getting source connection by ID:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

export { CONNECTION_TYPES };